/**
 * PLAT-11228 (audio-first): canvas plays the session's locally-held upload
 * while renditions are pending. Pure helpers — the orchestration lives with
 * the other audio-first steps in VideoCreatorStore.
 *
 * Mechanism: the renderer bridge's `cacheAsset(url, blob)` structured-clones
 * the raw File into the embed's own CacheStorage under a synthetic URL; the
 * composition then references that URL and the embed plays the cached bytes
 * without ever fetching it. Official SDK surface (see PLAT-11228 research).
 */

/**
 * Synthetic cache key for the local original. Never fetched when the cache
 * entry exists (bypass rules are cleared on every renderer init); if the
 * entry is ever evicted the element errors, and the next load repoints it
 * back to the audio-first source — fail-soft by design.
 */
const LOCAL_CANVAS_URL_PREFIX = 'https://local-preview.arborplatform.com/';

const DEFAULT_MAX_RENDERER_LOCAL_ASSET_MB = 1024;

function readMaxRendererLocalAssetMb(): number {
  const envValue = process.env.REACT_APP_MAX_RENDERER_LOCAL_ASSET_MB;
  if (envValue) {
    const parsed = Number(envValue);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return DEFAULT_MAX_RENDERER_LOCAL_ASSET_MB;
}

/**
 * `Renderer.cacheAsset` sends the Blob through `postMessage` to the
 * cross-origin Creatomate iframe. Multi-gigabyte uploads can exhaust the
 * renderer process while that message is cloned/cached, so large files stay
 * in the parent page and play through a native object-URL preview instead.
 * Overridable via `REACT_APP_MAX_RENDERER_LOCAL_ASSET_MB`.
 */
export const MAX_RENDERER_LOCAL_ASSET_BYTES =
  readMaxRendererLocalAssetMb() * 1024 * 1024;

export function requiresNativeLocalPreview(file: Pick<Blob, 'size'>): boolean {
  return file.size > MAX_RENDERER_LOCAL_ASSET_BYTES;
}

export function buildLocalCanvasUrl(storyId: string): string {
  return `${LOCAL_CANVAS_URL_PREFIX}sessions/${storyId}/local-original`;
}

export function isLocalCanvasSource(source: unknown): boolean {
  return (
    typeof source === 'string' && source.startsWith(LOCAL_CANVAS_URL_PREFIX)
  );
}

/**
 * Whether the browser can actually decode the local file's video track
 * (H.264/HEVC-in-supported-containers yes; ProRes and exotic MOVs no).
 * Undecodable or slow-to-probe files simply stay on the audio-first canvas.
 */
export function probeVideoDecodable(
  objectUrl: string,
  timeoutMs = 8000,
): Promise<boolean> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const timer = setTimeout(() => finish(false), timeoutMs);
    const finish = (ok: boolean) => {
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      resolve(ok);
    };
    video.onloadedmetadata = () =>
      finish(video.videoWidth > 0 && video.videoHeight > 0);
    video.onerror = () => finish(false);
    video.src = objectUrl;
  });
}

export type MediaLoadSettle = 'settled' | 'no-load' | 'timeout';

/**
 * Observe one renderer media-load cycle: wait (briefly) for the load to
 * start, then for it to finish. Lets the caller hold the pending veil until
 * the new source has actually painted instead of dropping it on the source
 * swap — which shows a black canvas for the load gap. Time-boxed; never
 * rejects. `timeout` means the load started but never completed within the
 * budget — the renderer is most likely wedged on that asset.
 */
export async function waitForMediaLoadSettle(
  isLoading: () => boolean,
  timeoutMs = 4000,
  armWindowMs = 600,
): Promise<MediaLoadSettle> {
  const started = Date.now();
  const tick = () => new Promise(resolve => setTimeout(resolve, 50));
  while (!isLoading()) {
    if (Date.now() - started >= armWindowMs) return 'no-load';
    await tick();
  }
  while (isLoading()) {
    if (Date.now() - started >= timeoutMs) return 'timeout';
    await tick();
  }
  return 'settled';
}

export interface WaitForRenditionOptions {
  /** How many probes before giving up (default 30 ≈ 90 s at 3 s). */
  maxAttempts?: number;
  /** Delay between probes in ms (default 3000). */
  intervalMs?: number;
  /** Abort early (component unmounted, story switched). */
  signal?: AbortSignal;
  /** Test seam. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * PLAT-11445 — the rendition hand-off probe both Creator Studio and the Clip
 * Editor use. populate lists a rendition URL before the bytes are readable,
 * so a swap issued at the pending flip can fail to load. `warm` is the
 * renderer's own fetch (WarmedRenderer.warmAsset): it follows the exact
 * CORS/signature path the canvas will use and leaves the bytes in the
 * renderer's cache, so the swap paints immediately. A page-side HEAD proves
 * nothing about the iframe's fetch and warms nothing. Resolves true when the
 * asset fetched, false when the budget ran out or the signal aborted.
 */
export async function waitForRenditionFetchable(
  warm: (url: string) => Promise<unknown>,
  url: string,
  {
    maxAttempts = 30,
    intervalMs = 3000,
    signal,
    sleep = ms => new Promise(resolve => setTimeout(resolve, ms)),
  }: WaitForRenditionOptions = {},
): Promise<boolean> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (signal?.aborted) return false;
    try {
      await warm(url);
      return true;
    } catch {
      /* not fetchable yet */
    }
    if (attempt < maxAttempts) await sleep(intervalMs);
  }
  return false;
}
